import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import Swal from "sweetalert2";
import {
  FaEdit,
  FaSearch,
  FaBox,
  FaChevronLeft,
  FaChevronRight,
  FaTrash,
  FaCheckSquare,
  FaCheckDouble,
  FaTimes,
} from "react-icons/fa";
import api from "../services/api";
import {
  getAllProductsFromDB,
  saveProductsToDB,
  upsertSingleProductInDB,
  deleteSingleProductFromDB,
  deleteMultipleProductsFromDB,
  getLastSyncTimeFromDB,
  getPendingSyncProductsFromDB,
} from "../services/dbService";
import Footer from "./Footer";
import ModernLoader from "./Loader";
import ProductModal from "./ProductModal";
import ProvisionalInvoiceModal from "./ProvisionalInvoiceModal";
import Header from "./Header";
import "./ProductPage.css";

const SearchableDropdown = ({ options, value, onChange, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const wrapperRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredOptions = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return options;
    return options.filter((opt) => opt.toLowerCase().includes(q));
  }, [options, search]);

  useEffect(() => {
    setHighlightedIndex(0);
  }, [search, isOpen]);

  useEffect(() => {
    if (isOpen && listRef.current) {
      const activeItem = listRef.current.children[highlightedIndex];
      if (activeItem && activeItem.scrollIntoView) {
        activeItem.scrollIntoView({ block: "nearest" });
      }
    }
  }, [highlightedIndex, isOpen]);

  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === "Enter" || e.key === " ") {
        setIsOpen(true);
        e.preventDefault();
      }
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((prev) => Math.min(prev + 1, filteredOptions.length));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlightedIndex === 0) {
        onChange("");
      } else if (filteredOptions[highlightedIndex - 1]) {
        onChange(filteredOptions[highlightedIndex - 1]);
      }
      setIsOpen(false);
      setSearch("");
    } else if (e.key === "Escape") {
      setIsOpen(false);
    }
  };

  return (
    <div ref={wrapperRef} className="pp-dropdownWrap">
      <div
        tabIndex={0}
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        className="pp-dropdownBtn"
      >
        <span
          className="pp-dropdownBtnText"
          style={{ color: value ? "#333" : "#888" }}
        >
          {value || placeholder}
        </span>
        <span
          style={{
            fontSize: "10px",
            color: "#666",
            transform: isOpen ? "rotate(180deg)" : "none",
            transition: "transform 0.2s",
          }}
        >
          ▼
        </span>
      </div>

      {isOpen && (
        <div className="pp-dropdownMenu">
          <input
            autoFocus
            type="text"
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={handleKeyDown}
            style={{
              margin: "8px",
              padding: "8px 12px",
              border: "1px solid #ddd",
              borderRadius: "6px",
              outline: "none",
              fontSize: "14px",
            }}
          />
          <div
            ref={listRef}
            style={{ overflowY: "auto", flex: 1, paddingBottom: "4px" }}
          >
            <div
              onClick={() => {
                onChange("");
                setIsOpen(false);
                setSearch("");
              }}
              onMouseEnter={() => setHighlightedIndex(0)}
              style={{
                padding: "10px 12px",
                cursor: "pointer",
                borderBottom: "1px solid #eee",
                background:
                  highlightedIndex === 0
                    ? "#e5e7eb"
                    : value === ""
                    ? "#f3f4f6"
                    : "#fff",
                fontSize: "14px",
                color: "#333",
              }}
            >
              {placeholder}
            </div>
            {filteredOptions.length === 0 ? (
              <div
                style={{
                  padding: "12px",
                  color: "#999",
                  textAlign: "center",
                  fontSize: "14px",
                }}
              >
                No results
              </div>
            ) : (
              filteredOptions.map((opt, idx) => (
                <div
                  key={opt}
                  onClick={() => {
                    onChange(opt);
                    setIsOpen(false);
                    setSearch("");
                  }}
                  onMouseEnter={() => setHighlightedIndex(idx + 1)}
                  style={{
                    padding: "10px 12px",
                    cursor: "pointer",
                    background:
                      highlightedIndex === idx + 1
                        ? "#e5e7eb"
                        : value === opt
                        ? "#f3f4f6"
                        : "#fff",
                    fontSize: "14px",
                    color: "#333",
                  }}
                >
                  {opt}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

function ProductPage({ user, onLogout }) {
  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Price mode
  const [priceMode, setPriceMode] = useState("W"); // "W" | "X"

  const [filters, setFilters] = useState({
    productName: "",
    design: "",
    company: "",
  });

  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);

  // Sync / Offline status: 'synced' | 'syncing' | 'offline'
  const [syncStatus, setSyncStatus] = useState(
    typeof navigator !== "undefined" && !navigator.onLine ? "offline" : "synced"
  );
  const [isOffline, setIsOffline] = useState(
    typeof navigator !== "undefined" ? !navigator.onLine : false
  );
  const isOfflineRef = useRef(
    typeof navigator !== "undefined" ? !navigator.onLine : false
  );

  const [uploading, setUploading] = useState(false);
  const [showActionMenu, setShowActionMenu] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showInvoice, setShowInvoice] = useState(false);

  const fileInputRef = useRef(null);
  const actionMenuRef = useRef(null);

  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [isSelectMode, setIsSelectMode] = useState(false);
  const isCurrentlyOffline =
    isOffline || syncStatus === "offline" || (typeof navigator !== "undefined" && !navigator.onLine);

  // Pre-extracted dropdown filter lists
  const [productNameList, setProductNameList] = useState([]);
  const [companyList, setCompanyList] = useState([]);

  // Date helpers
  const todayStr = new Date().toISOString().slice(0, 10);

  const toInputDate = (v) => {
    if (!v) return "";
    if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}/.test(v)) return v.slice(0, 10);
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return "";
    return d.toISOString().slice(0, 10);
  };

  const formatDate = (v) => {
    if (!v) return "-";
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return "-";
    return d.toLocaleDateString("en-IN");
  };

  // Form (modal)
  const [form, setForm] = useState({
    productName: "",
    designNumber: "",
    date: todayStr,
    wRate: 0,
    xRate: 0,
    isActive: true,
    createdOn: new Date().toISOString(),
    createdBy: user?.username || "Admin",
    modifiedOn: null,
    modifiedBy: null,
  });

  const norm = (v) => (v === null || v === undefined ? "" : String(v)).trim().toLowerCase();
  const cleanStr = (s) => (s ? String(s).toLowerCase().replace(/[^a-z0-9]/g, "") : "");

  const getSelectedRate = (item) => {
    const w = Number(item?.wRate ?? 0);
    const x = Number(item?.xRate ?? 0);
    return priceMode === "W" ? (Number.isFinite(w) ? w : 0) : (Number.isFinite(x) ? x : 0);
  };

  const setFilterValue = (key, value) => {
    setSearch("");
    setFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  // Search input debounce (150ms) to ensure smooth 60fps typing on 100,000 records
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setCurrentPage(1);
    }, 150);
    return () => clearTimeout(timer);
  }, [search]);

  // Click outside listener for action menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (actionMenuRef.current && !actionMenuRef.current.contains(event.target)) {
        setShowActionMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Helper to extract unique names and companies for filters
  const updateFilterLists = useCallback((products) => {
    const productNames = [
      ...new Set(
        products.map((p) => p.productName?.trim().toUpperCase()).filter(Boolean)
      ),
    ].sort();

    const companies = [
      ...new Set(
        products
          .map((p) => {
            if (!p.designNumber) return "";
            if (p.designNumber.includes("-")) return p.designNumber.split("-")[0].trim();
            if (p.designNumber.includes(" ")) return p.designNumber.split(" ")[0].trim();
            return p.designNumber.trim();
          })
          .filter(Boolean)
      ),
    ].sort();

    setProductNameList(productNames);
    setCompanyList(companies);
  }, []);


  // ============================================================
  // INSTANT LOADING & DELTA SYNC ENGINE (100,000+ Records Offline)
  // ============================================================
  const syncWithServer = useCallback(async (currentLocalData) => {
    // If browser is already offline, set offline immediately and do not hang
    if (typeof navigator !== "undefined" && !navigator.onLine) {
      setSyncStatus("offline");
      setIsOffline(true);
      return;
    }

    try {
      // Step A: Upload any items created/modified offline first
      try {
        const pendingItems = await getPendingSyncProductsFromDB();
        if (pendingItems && pendingItems.length > 0) {
          for (const item of pendingItems) {
            const isTemp = String(item.id).startsWith("offline_");
            const payload = {
              productName: item.productName,
              designNumber: item.designNumber,
              date: item.date,
              wRate: item.wRate,
              xRate: item.xRate,
              isActive: item.isActive,
            };
            if (isTemp) {
              const res = await api.post("/Product", {
                ...payload,
                createdBy: item.createdBy || "Admin",
              }, { silent: true, timeout: 20000 });
              if (res.data?.data?.id) {
                await deleteSingleProductFromDB(item.id);
                await upsertSingleProductInDB(res.data.data);
              }
            } else {
              await api.put(`/Product/${item.id}`, {
                ...payload,
                id: item.id,
                modifiedOn: new Date().toISOString(),
                modifiedBy: item.modifiedBy || "Admin",
              }, { silent: true, timeout: 20000 });
              await upsertSingleProductInDB({ ...item, _pendingSync: false });
            }
          }
        }
      } catch (uploadErr) {
        console.warn("Offline pending sync upload error:", uploadErr?.message);
      }

      // Step B: Pull delta updates from server
      const lastSyncTime = await getLastSyncTimeFromDB();
      const isDelta = currentLocalData.length > 0 && !!lastSyncTime;
      const syncUrl = isDelta
        ? `/Product/sync?since=${encodeURIComponent(lastSyncTime)}`
        : "/Product/sync";

      // 30s timeout allows slow mobile networks (Slow 4G / 3G) to finish sync without aborting
      const res = await api.get(syncUrl, { silent: true, timeout: 30000 });
      const syncData = res.data?.data;

      if (!syncData) {
        setSyncStatus("synced");
        setIsOffline(false);
        return;
      }

      const incomingList = syncData.products || [];
      const activeIds = syncData.activeIds;
      const serverTime = syncData.serverTime || new Date().toISOString();

      if (isDelta) {
        let merged = [...currentLocalData];

        // 1. Automatic Database Reconciliation:
        // Purge any locally cached product that was deleted from the database
        if (Array.isArray(activeIds)) {
          const activeIdSet = new Set(activeIds);
          const orphanedItems = currentLocalData.filter(
            (item) => !activeIdSet.has(item.id) && !String(item.id).startsWith("offline_")
          );

          if (orphanedItems.length > 0) {
            console.log(`Auto-purging ${orphanedItems.length} deleted items from IndexedDB...`);
            for (const item of orphanedItems) {
              await deleteSingleProductFromDB(item.id);
            }
            merged = merged.filter(
              (item) => activeIdSet.has(item.id) || String(item.id).startsWith("offline_")
            );
          }
        }

        // 2. Merge incoming delta updates into local state
        if (incomingList.length > 0) {
          const updateMap = new Map();
          incomingList.forEach((item) => updateMap.set(item.id, item));

          merged = merged.map((item) =>
            updateMap.has(item.id) ? updateMap.get(item.id) : item
          );

          // Add brand new records
          incomingList.forEach((item) => {
            if (!merged.some((existing) => existing.id === item.id)) {
              merged.push(item);
            }
          });

          // Save delta into IndexedDB
          await saveProductsToDB(incomingList, true, serverTime);
        } else {
          // Server has no new updates since last sync
          await saveProductsToDB([], true, serverTime);
        }

        setData(merged);
        updateFilterLists(merged);
      } else {
        // Initial Full Load -> Store full dataset into IndexedDB
        await saveProductsToDB(incomingList, false, serverTime);
        setData(incomingList);
        updateFilterLists(incomingList);
      }

      setSyncStatus("synced");
      setIsOffline(false);
      isOfflineRef.current = false;
    } catch (err) {
      console.warn("Background delta sync skipped/failed:", err?.message);
      // Keep working with local IndexedDB data. Never force offline if browser is online!
      if (typeof navigator !== "undefined" && !navigator.onLine) {
        setSyncStatus("offline");
        setIsOffline(true);
        isOfflineRef.current = true;
      } else {
        setSyncStatus("synced");
        setIsOffline(false);
        isOfflineRef.current = false;
      }
    }
  }, [updateFilterLists]);

  const handleOffline = useCallback(() => {
    isOfflineRef.current = true;
    setIsOffline(true);
    setSyncStatus("offline");

    // Show prominent offline warning modal
    Swal.close();
    Swal.fire({
      icon: "warning",
      title: "You are Offline!",
      html: `
        <div style="font-size: 15px; line-height: 1.5; text-align: center; color: #374151;">
          <p style="font-size: 15px; font-weight: 600; color: #dc2626; margin-bottom: 6px;">
            ⚠️ Internet connection band ho gaya hai.
          </p>
          <p style="margin: 0; color: #4b5563;">
            App offline mode me chal raha hai. Data safe rakhne ke liye <strong>Page Refresh na karein</strong>.
          </p>
        </div>
      `,
      confirmButtonText: "Theek Hai",
      confirmButtonColor: "#ea580c",
      allowOutsideClick: true,
    });
  }, []);

  const handleOnline = useCallback(async () => {
    isOfflineRef.current = false;
    setIsOffline(false);
    setSyncStatus("synced");

    Swal.close();
    Swal.fire({
      toast: true,
      position: "top-end",
      icon: "success",
      title: "Online 🟢",
      text: "Internet wapas aa gaya hai.",
      showConfirmButton: false,
      timer: 3000,
    });

    // Auto-sync in background when internet reconnects
    try {
      const currentLocalData = await getAllProductsFromDB();
      if (currentLocalData && currentLocalData.length > 0) {
        syncWithServer(currentLocalData);
      }
    } catch (e) {
      console.warn("Background delta sync skipped on reconnect:", e?.message);
    }
  }, [syncWithServer]);

  const checkConnection = useCallback(async () => {
    const browserOnline = typeof navigator !== "undefined" ? navigator.onLine : true;
    if (!browserOnline) {
      if (!isOfflineRef.current) {
        handleOffline();
      }
      return false;
    }

    try {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 2500);
      await fetch(`https://www.google.com/favicon.ico?_t=${Date.now()}`, {
        method: "HEAD",
        mode: "no-cors",
        cache: "no-store",
        signal: controller.signal,
      });
      clearTimeout(timer);

      if (isOfflineRef.current) {
        handleOnline();
      }
      return true;
    } catch {
      if (!isOfflineRef.current) {
        handleOffline();
      }
      return false;
    }
  }, [handleOffline, handleOnline]);

  const loadData = useCallback(async () => {
    let cached = [];
    try {
      // Step 1: Instant load from IndexedDB local memory (< 50ms!)
      cached = await getAllProductsFromDB();
      if (cached && cached.length > 0) {
        setData(cached);
        updateFilterLists(cached);
      }
    } catch (err) {
      console.warn("DB load warning:", err);
    } finally {
      // Instantly dismiss any loader on first tick!
      setLoading(false);
    }

    // Step 2: Check offline state
    if (typeof navigator !== "undefined" && !navigator.onLine) {
      handleOffline();
      return;
    }

    // Step 3: Silent background delta sync without blocking UI or showing any loader
    try {
      await syncWithServer(cached || []);
    } catch (syncErr) {
      console.warn("Silent background sync skipped:", syncErr?.message);
    }
  }, [syncWithServer, updateFilterLists, handleOffline]);

  // 1. Initial mount data load: Runs strictly ONCE on mount
  useEffect(() => {
    loadData();
  }, [loadData]);

  // 2. Real-Time Online / Offline Event Listeners & Periodic WAN Monitor
  useEffect(() => {
    const onWindowOffline = () => {
      handleOffline();
    };

    const onWindowOnline = () => {
      handleOnline();
    };

    window.addEventListener("offline", onWindowOffline);
    window.addEventListener("online", onWindowOnline);
    window.addEventListener("focus", checkConnection);

    const intervalId = setInterval(checkConnection, 5000);

    return () => {
      window.removeEventListener("offline", onWindowOffline);
      window.removeEventListener("online", onWindowOnline);
      window.removeEventListener("focus", checkConnection);
      clearInterval(intervalId);
    };
  }, [handleOffline, handleOnline, checkConnection]);

  // Page Refresh Guard: Prevent accidental refresh or window close when offline
  useEffect(() => {
    const isOfflineNow = typeof navigator !== "undefined" && !navigator.onLine ? true : isOffline;

    const handleBeforeUnload = (e) => {
      if (isOfflineNow) {
        e.preventDefault();
        e.returnValue = "You are offline! Page refresh karne se data loss ho sakta hai.";
        return e.returnValue;
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isOffline]);

  // Keyboard Refresh Guard: Intercept F5 / Ctrl+R / Cmd+R when offline and alert user
  useEffect(() => {
    const isOfflineNow = typeof navigator !== "undefined" && !navigator.onLine ? true : isOffline;

    const handleKeyDown = (e) => {
      if (!isOfflineNow) return;

      const isF5 = e.key === "F5";
      const isReloadShortcut =
        (e.ctrlKey || e.metaKey) && (e.key === "r" || e.key === "R");

      if (isF5 || isReloadShortcut) {
        e.preventDefault();
        e.stopPropagation();

        Swal.fire({
          icon: "warning",
          title: "You are offline!",
          html: `
            <div style="text-align: center; font-size: 14px; line-height: 1.5;">
              <p style="margin: 0; color: #dc2626; font-weight: 600;">
                Internet connection band hai. Page Refresh block kiya gaya hai.
              </p>
            </div>
          `,
          confirmButtonColor: "#ea580c",
          confirmButtonText: "Theek Hai",
        });
      }
    };

    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
  }, [isOffline, syncStatus]);

  const resetForm = () => {
    setForm({
      productName: "",
      designNumber: "",
      date: todayStr,
      wRate: 0,
      xRate: 0,
      isActive: true,
      createdOn: new Date().toISOString(),
      createdBy: user?.username || "Admin",
      modifiedOn: null,
      modifiedBy: null,
    });
    setEditId(null);
  };

  const handleSave = async () => {
    if (!form.productName || !form.designNumber) {
      Swal.fire({
        icon: "warning",
        title: "Required Fields!",
        text: "Please fill Product Name and Design Number",
        confirmButtonColor: "#6366f1",
      });
      return;
    }

    if (form.date && form.date > todayStr) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Date",
        text: "Future date allowed nahi hai.",
        confirmButtonColor: "#6366f1",
      });
      return;
    }

    if (isSaving) return;
    setIsSaving(true);

    const payload = {
      ...form,
      date: form.date ? new Date(`${form.date}T00:00:00.000Z`).toISOString() : null,
      wRate: Number(form.wRate) || 0,
      xRate: Number(form.xRate) || 0,
    };

    const isCurrentlyOffline = isOffline || syncStatus === "offline" || !navigator.onLine;

    // Fast-path: When user is already offline, save locally without waiting for network timeout
    if (isCurrentlyOffline) {
      try {
        let savedProduct;
        if (editId) {
          savedProduct = {
            ...payload,
            id: editId,
            modifiedOn: new Date().toISOString(),
            modifiedBy: user?.username || "Admin",
            _pendingSync: true,
          };
          setData((prev) =>
            prev.map((item) => (item.id === editId ? { ...item, ...savedProduct } : item))
          );
          await upsertSingleProductInDB(savedProduct);
        } else {
          const tempId = `offline_${Date.now()}`;
          savedProduct = {
            ...payload,
            id: tempId,
            createdBy: user?.username || "Admin",
            _pendingSync: true,
          };
          setData((prev) => [savedProduct, ...prev]);
          await upsertSingleProductInDB(savedProduct);
        }

        setShowModal(false);
        resetForm();
        Swal.fire({
          icon: "info",
          title: "Saved to Local Memory 💾",
          text: "Aapka product local storage me surakshit save ho gaya hai. Internet wapas aane par automatic server se sync ho jayega.",
          confirmButtonColor: "#6366f1",
        });
      } catch (err) {
        console.error("Local save failed", err);
      } finally {
        setIsSaving(false);
      }
      return;
    }

    try {
      let savedProduct;
      if (editId) {
        const res = await api.put(`/Product/${editId}`, {
          ...payload,
          modifiedOn: new Date().toISOString(),
          modifiedBy: user?.username || "Admin",
        });
        savedProduct = res.data?.data || { ...payload, id: editId };

        // Update in-memory state & IndexedDB immediately
        setData((prev) =>
          prev.map((item) => (item.id === editId ? { ...item, ...savedProduct } : item))
        );
        await upsertSingleProductInDB({ ...savedProduct, id: editId });
      } else {
        const res = await api.post("/Product", {
          ...payload,
          createdBy: user?.username || "Admin",
        });
        savedProduct = res.data?.data || payload;

        if (savedProduct && savedProduct.id) {
          setData((prev) => [savedProduct, ...prev]);
          await upsertSingleProductInDB(savedProduct);
        } else {
          // If id not in response, refresh from DB
          await loadData();
        }
      }

      setShowModal(false);
      resetForm();
    } catch (err) {
      console.warn("Online save failed, saving into local offline memory:", err);
      // Network failure fallback: persist locally so work is not lost
      const tempId = editId || `offline_${Date.now()}`;
      const savedProduct = {
        ...payload,
        id: tempId,
        _pendingSync: true,
      };

      if (editId) {
        setData((prev) =>
          prev.map((item) => (item.id === editId ? { ...item, ...savedProduct } : item))
        );
      } else {
        setData((prev) => [savedProduct, ...prev]);
      }
      await upsertSingleProductInDB(savedProduct);

      setShowModal(false);
      resetForm();
      Swal.fire({
        icon: "warning",
        title: "Network Error: Saved Locally",
        text: "Server se connect nahi ho paya. Data local storage me save kar diya gaya hai.",
        confirmButtonColor: "#f59e0b",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = (item) => {
    if (isCurrentlyOffline) {
      Swal.fire({
        icon: "warning",
        title: "Offline Mode",
        text: "Offline mode mein product edit karna allowed nahi hai. Kripya internet connect karein.",
        confirmButtonColor: "#6366f1",
      });
      return;
    }

    setForm({
      productName: item.productName || "",
      designNumber: item.designNumber || "",
      date: toInputDate(item.date) || todayStr,
      wRate: Number(item.wRate ?? 0),
      xRate: Number(item.xRate ?? 0),
      isActive: item.isActive,
      createdOn: item.createdOn,
      createdBy: item.createdBy,
      modifiedOn: item.modifiedOn,
      modifiedBy: item.modifiedBy,
    });
    setEditId(item.id);
    setShowModal(true);
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
  };

  const toggleSelectMode = () => {
    setIsSelectMode((prev) => {
      const next = !prev;
      if (!next) {
        clearSelection(); // Exit selection mode clears selection
      }
      return next;
    });
  };

  const toggleSelectOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    const currentIds = currentData.map((p) => p.id);
    const allSelectedOnPage =
      currentIds.length > 0 && currentIds.every((id) => selectedIds.has(id));

    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelectedOnPage) {
        currentIds.forEach((id) => next.delete(id));
      } else {
        currentIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const handleDelete = async (product) => {
    const isTemp = String(product.id).startsWith("offline_");

    if (isCurrentlyOffline && !isTemp) {
      Swal.fire({
        icon: "warning",
        title: "Offline Mode",
        text: "Offline mode mein server products delete karna allowed nahi hai. Kripya internet connect karein.",
        confirmButtonColor: "#6366f1",
      });
      return;
    }

    const pName = product.productName || product.designNumber || "Is product";
    const result = await Swal.fire({
      title: "Delete Confirmation",
      html: `<p style="font-size:15px;color:#374151;margin:0 0 10px 0;">Kya aap sach me <b>"${pName}"</b> ko delete karna chahte hain?</p>
             <p style="color:#ef4444;font-size:13px;font-weight:600;margin:0;">⚠️ Ye action permanently delete karegi aur undo nahi ho sakti.</p>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
      focusCancel: true,
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    try {
      setLoading(true);

      // Only call server DELETE if this is a real synced server product
      if (!isTemp) {
        await api.delete(`/Product/${product.id}`);
      }

      // Update state immediately
      setData((prev) => prev.filter((p) => p.id !== product.id));

      // Remove from selection if present
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(product.id);
        return next;
      });

      // Remove from local IndexedDB
      await deleteSingleProductFromDB(product.id);

      Swal.fire({
        icon: "success",
        title: "Deleted!",
        text: "Product successfully delete ho gaya hai.",
        timer: 1800,
        showConfirmButton: false,
      });
    } catch (err) {
      console.error("Delete error:", err);
      Swal.fire({
        icon: "error",
        title: "Delete Failed",
        text: err.response?.data?.message || err.message || "Product delete nahi ho saka.",
        confirmButtonColor: "#6366f1",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    const idsToDelete = Array.from(selectedIds);
    if (idsToDelete.length === 0) {
      Swal.fire({
        icon: "info",
        title: "No Products Selected",
        text: "Kripya pehle delete karne ke liye products select karein.",
        confirmButtonColor: "#6366f1",
      });
      return;
    }

    const realIds = idsToDelete.filter((id) => !String(id).startsWith("offline_"));

    if (isCurrentlyOffline && realIds.length > 0) {
      Swal.fire({
        icon: "warning",
        title: "Offline Mode",
        text: "Offline mode mein server products delete karna allowed nahi hai. Kripya internet connect karein.",
        confirmButtonColor: "#6366f1",
      });
      return;
    }

    const result = await Swal.fire({
      title: "Bulk Delete Confirmation",
      html: `<p style="font-size:15px;color:#374151;margin:0 0 10px 0;">Kya aap sach me in <b>${idsToDelete.length} selected products</b> ko permanently delete karna chahte hain?</p>
             <p style="color:#ef4444;font-size:13px;font-weight:600;margin:0;">⚠️ Ye saare records database aur local memory se permanently remove ho jayenge.</p>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
      confirmButtonText: `Yes, Delete All (${idsToDelete.length})`,
      cancelButtonText: "Cancel",
      focusCancel: true,
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    try {
      setLoading(true);

      // Only send real server IDs to the bulk-delete API
      if (realIds.length > 0) {
        await api.post("/Product/bulk-delete", realIds);
      }

      // Update state immediately
      const idSet = new Set(idsToDelete);
      setData((prev) => prev.filter((p) => !idSet.has(p.id)));

      // Clear selection & exit select mode
      clearSelection();
      setIsSelectMode(false);

      // Remove from local IndexedDB
      await deleteMultipleProductsFromDB(idsToDelete);

      Swal.fire({
        icon: "success",
        title: "Deleted!",
        text: `${idsToDelete.length} products successfully delete ho gaye hain.`,
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (err) {
      console.error("Bulk delete error:", err);
      Swal.fire({
        icon: "error",
        title: "Bulk Delete Failed",
        text: err.response?.data?.message || err.message || "Bulk delete operation fail ho gaya.",
        confirmButtonColor: "#6366f1",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!fileInputRef.current?.files?.[0]) {
      Swal.fire({
        icon: "warning",
        title: "No File",
        text: "Please select an Excel file",
        confirmButtonColor: "#6366f1",
      });
      return;
    }

    const file = fileInputRef.current.files[0];
    const formData = new FormData();
    formData.append("file", file);

    setUploading(true);
    try {
      const token = localStorage.getItem("token") || sessionStorage.getItem("token");
      const config = { headers: { "Content-Type": "multipart/form-data" } };
      if (token) config.headers["Authorization"] = `Bearer ${token}`;

      await api.post("/Product/upload-excel", formData, config);

      fileInputRef.current.value = "";
      setShowActionMenu(false);

      // Force full reload & update IndexedDB cache
      const res = await api.get("/Product/sync");
      const products = res.data?.data?.products || [];
      const serverTime = res.data?.data?.serverTime || new Date().toISOString();

      await saveProductsToDB(products, false, serverTime);
      setData(products);
      updateFilterLists(products);
    } catch (err) {
      console.error("Upload failed", err);
    } finally {
      setUploading(false);
    }
  };

  const handleLogout = () => {
    Swal.fire({
      title: "Logout?",
      text: "Are you sure you want to logout?",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#6366f1",
      cancelButtonColor: "#ef4444",
      confirmButtonText: "Yes, Logout",
    }).then((result) => {
      if (result.isConfirmed) onLogout();
    });
  };

  // High-Speed Fuzzy & Normalized Filter for 100,000 items
  const filtered = useMemo(() => {
    const rawSearch = debouncedSearch.trim();
    const cleanFullQuery = cleanStr(rawSearch);
    const searchWords = rawSearch.toLowerCase().split(/\s+/).filter(Boolean);
    const cleanWords = searchWords.map(cleanStr).filter(Boolean);

    const pn = norm(filters.productName);
    const cleanPn = cleanStr(pn);

    const des = norm(filters.design);
    const cleanDes = cleanStr(des);

    const comp = norm(filters.company);
    const cleanComp = cleanStr(comp);

    const hasSearch = !!cleanFullQuery || searchWords.length > 0;
    const hasPn = !!cleanPn;
    const hasDes = !!cleanDes;
    const hasComp = !!cleanComp;

    // Fast-path: no filter applied
    if (!hasSearch && !hasPn && !hasDes && !hasComp) {
      return data.filter((item) => !item || (item.isDeleted !== true && item.IsDeleted !== true));
    }

    return data.filter((item) => {
      if (item && (item.isDeleted === true || item.IsDeleted === true)) return false;
      // 1. Intelligent Search Match
      // Handles:
      // a) Without spaces/special chars (e.g. FINEANISHKA1312109 matching FINE- ANISHKA- 13 12109)
      // b) Multiple words with spaces (e.g. FINE 12109 matching FINE- ANISHKA- 13 12109)
      // c) Exact raw substrings
      if (hasSearch) {
        const rawKey =
          item._searchKey ||
          `${item.productName || ""} ${item.designNumber || ""}`.toLowerCase();
        const cleanKey = item._cleanKey || cleanStr(rawKey);

        const matchesCleanFull = cleanFullQuery && cleanKey.includes(cleanFullQuery);
        const matchesAllCleanWords =
          cleanWords.length > 0 && cleanWords.every((cw) => cleanKey.includes(cw));
        const matchesAllRawWords =
          searchWords.length > 0 && searchWords.every((w) => rawKey.includes(w));

        if (!matchesCleanFull && !matchesAllCleanWords && !matchesAllRawWords) {
          return false;
        }
      }

      // 2. Product Name filter (normalized)
      if (hasPn) {
        const itemCleanPn = cleanStr(item.productName);
        if (!itemCleanPn.includes(cleanPn)) {
          return false;
        }
      }

      // 3. Design filter (normalized)
      if (hasDes) {
        const itemCleanDes = cleanStr(item.designNumber);
        if (!itemCleanDes.includes(cleanDes)) {
          return false;
        }
      }

      // 4. Company filter (normalized)
      if (hasComp) {
        let itemCompany = "";
        const dn = item.designNumber;
        if (dn) {
          if (dn.includes("-")) itemCompany = dn.split("-")[0].trim();
          else if (dn.includes(" ")) itemCompany = dn.split(" ")[0].trim();
          else itemCompany = dn.trim();
        }
        if (cleanStr(itemCompany) !== cleanComp) return false;
      }

      return true;
    });
  }, [data, debouncedSearch, filters]);

  const isAllSelected = itemsPerPage === -1;
  const totalPages = isAllSelected ? 1 : Math.max(1, Math.ceil(filtered.length / itemsPerPage));

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
    if (currentPage < 1) setCurrentPage(1);
  }, [currentPage, totalPages]);

  const indexOfLast = isAllSelected ? filtered.length : currentPage * itemsPerPage;
  const indexOfFirst = isAllSelected ? 0 : indexOfLast - itemsPerPage;
  const currentData = isAllSelected ? filtered : filtered.slice(indexOfFirst, indexOfLast);

  if (loading && data.length === 0) {
    return (
      <div className="pp-loadingScreen">
        <ModernLoader isLoading={true} message="Loading MT Products..." />
      </div>
    );
  }

  return (
    <div className="pp-page">
      <ModernLoader
        isLoading={isSaving || uploading}
        message={
          isSaving
            ? editId
              ? "Updating Product..."
              : "Saving Product..."
            : "Uploading File..."
        }
      />

      <Header
        user={user}
        onLogout={handleLogout}
        onAdd={() => {
          resetForm();
          setShowModal(true);
        }}
        onGenerateBilling={() => setShowInvoice(true)}
        uploading={uploading}
        handleUpload={handleUpload}
        fileInputRef={fileInputRef}
        showActionMenu={showActionMenu}
        setShowActionMenu={setShowActionMenu}
        actionMenuRef={actionMenuRef}
        syncStatus={isOffline ? "offline" : syncStatus}
        totalCount={data.length}
        onCheckStatus={checkConnection}
        isOffline={isOffline}
      />

      {/* Prominent Offline & Refresh Guard Sticky Banner */}
      {isOffline && (
        <div className="pp-offlineAlertBanner" role="alert">
          <div className="pp-offlineAlertContent">
            <span className="pp-offlinePulse" title="Disconnected"></span>
            <div className="pp-offlineAlertText">
              <strong>⚠️ Offline:</strong> Internet connection band hai. Data local memory me safe hai — <u>Page Refresh na karein</u>.
            </div>
          </div>
        </div>
      )}

      <main className="pp-container">
        {/* Toolbar */}
        <div className="pp-toolbar">
          <div className="pp-toolbarTop">
            <div className="pp-dropdownsRow">
              <SearchableDropdown
                options={productNameList}
                value={filters.productName}
                onChange={(val) => setFilterValue("productName", val)}
                placeholder="All Products"
              />

              <SearchableDropdown
                options={companyList}
                value={filters.company}
                onChange={(val) => setFilterValue("company", val)}
                placeholder="All Companies"
              />
            </div>

            <div className="pp-searchWrap">
              <FaSearch className="pp-searchIcon" />
              <input
                className="pp-input pp-searchInput"
                type="text"
                placeholder="Search across 100k+ products..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <div className="pp-mode">
              <label className="pp-radio">
                <input
                  type="radio"
                  name="priceMode"
                  checked={priceMode === "W"}
                  onChange={() => {
                    setPriceMode("W");
                    setCurrentPage(1);
                  }}
                />
                W Rate
              </label>

              <label className="pp-radio">
                <input
                  type="radio"
                  name="priceMode"
                  checked={priceMode === "X"}
                  onChange={() => {
                    setPriceMode("X");
                    setCurrentPage(1);
                  }}
                />
                X Rate
              </label>
            </div>
          </div>
        </div>

        {/* Meta Info & Selection Mode Trigger */}
        <div className="pp-meta">
          <div className="pp-metaLeft">
            <p className="pp-metaText">
              Showing {filtered.length === 0 ? 0 : indexOfFirst + 1}-
              {Math.min(indexOfLast, filtered.length)} of {filtered.length.toLocaleString()}{" "}
              records
            </p>
            <p className="pp-metaText">
              Mode: <b>{priceMode === "W" ? "W Rate" : "X Rate"}</b>
            </p>
          </div>

          <div className="pp-metaRight">
            <button
              type="button"
              className={`pp-selectToggleBtn ${isSelectMode ? "active" : ""}`}
              onClick={toggleSelectMode}
              title={isSelectMode ? "Close selection mode" : "Select products"}
            >
              <FaCheckSquare size={13} />
              {isSelectMode ? "Close Selection" : "Select Products"}
            </button>
          </div>
        </div>

        {/* Selection Toolbar (Shown when isSelectMode is active) */}
        {isSelectMode && (
          <div className="pp-selectionBar">
            <div className="pp-selectionBarLeft">
              <button
                type="button"
                className="pp-selectionActionBtn"
                onClick={toggleSelectAll}
                title="Select or deselect all items on this page"
              >
                <FaCheckDouble size={13} />
                {currentData.length > 0 &&
                currentData.every((p) => selectedIds.has(p.id))
                  ? "Deselect Page"
                  : "Select All"}
              </button>
              <span className="pp-selectionCountBadge">
                {selectedIds.size} selected
              </span>
            </div>

            <div className="pp-selectionBarRight">
              <button
                type="button"
                className="pp-bulkDeleteBtn"
                onClick={handleBulkDelete}
                disabled={selectedIds.size === 0 || isCurrentlyOffline}
                title={
                  isCurrentlyOffline
                    ? "Offline mode mein delete disabled hai"
                    : selectedIds.size === 0
                    ? "Pehle products select karein"
                    : "Delete selected products"
                }
              >
                <FaTrash size={12} />
                Delete Selected ({selectedIds.size})
              </button>

              <button
                type="button"
                className="pp-selectionCancelBtn"
                onClick={toggleSelectMode}
                title="Cancel selection"
              >
                <FaTimes size={12} />
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Data Presentation */}
        {currentData.length > 0 ? (
          <>
            {/* Mobile + Tab: Card Box view */}
            <div className="pp-grid pp-onlyMobile">
              {currentData.map((p) => (
                <div
                  className={`pp-product ${
                    priceMode === "W" ? "pp-productW" : "pp-productX"
                  } ${isSelectMode && selectedIds.has(p.id) ? "pp-productSelected" : ""}`}
                  key={p.id}
                >
                  <div className="pp-productHead">
                    <div className="pp-cardHeadLeft">
                      {isSelectMode && (
                        <input
                          type="checkbox"
                          className="pp-checkbox"
                          checked={selectedIds.has(p.id)}
                          onChange={() => toggleSelectOne(p.id)}
                          aria-label={`Select ${p.productName || p.designNumber}`}
                        />
                      )}
                      <div className="pp-company">
                        {p.productName ? (
                          <p className="pp-productName">{p.productName}</p>
                        ) : null}
                      </div>
                    </div>

                    <div className="pp-actionBtns">
                      <button
                        className="pp-editBtn"
                        onClick={() => handleEdit(p)}
                        type="button"
                        disabled={isCurrentlyOffline}
                        title={
                          isCurrentlyOffline
                            ? "Offline mode mein edit disabled hai"
                            : "Edit product"
                        }
                      >
                        <FaEdit size={12} />
                        Edit
                      </button>
                      <button
                        className="pp-deleteBtn"
                        onClick={() => handleDelete(p)}
                        type="button"
                        disabled={isCurrentlyOffline}
                        title={
                          isCurrentlyOffline
                            ? "Offline mode mein delete disabled hai"
                            : "Delete product"
                        }
                      >
                        <FaTrash size={11} />
                        Delete
                      </button>
                    </div>
                  </div>

                  <div className="pp-kvBox pp-kvBoxAlways2">
                    <div className="pp-kv">
                      <div className="pp-k">C Name / Design Number</div>
                      <div className="pp-v">{p.designNumber || "-"}</div>
                    </div>

                    <div className="pp-kv">
                      <div className="pp-k">Date</div>
                      <div className="pp-v">{formatDate(p.date)}</div>
                    </div>
                  </div>

                  <div className="pp-foot">
                    <span
                      className={`pp-ratePill ${
                        priceMode === "W" ? "pp-ratePillW" : "pp-ratePillX"
                      }`}
                    >
                      {priceMode === "W" ? "W Rate" : "X Rate"} : ₹
                      {getSelectedRate(p).toLocaleString("en-IN")}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Laptop + Desktop: High Performance Table view */}
            <div className="pp-tableWrap pp-onlyDesktop">
              <table className="pp-table">
                <thead>
                  <tr>
                    {isSelectMode && (
                      <th style={{ width: "42px", textAlign: "center" }}>
                        <input
                          type="checkbox"
                          className="pp-checkbox"
                          checked={
                            currentData.length > 0 &&
                            currentData.every((p) => selectedIds.has(p.id))
                          }
                          onChange={toggleSelectAll}
                          title="Select All on this page"
                        />
                      </th>
                    )}
                    <th>Product</th>
                    <th>C Name / Design Number</th>
                    <th>Date</th>
                    <th>{priceMode === "W" ? "W Rate" : "X Rate"}</th>
                    <th>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {currentData.map((p) => (
                    <tr
                      key={p.id}
                      className={isSelectMode && selectedIds.has(p.id) ? "pp-rowSelected" : ""}
                    >
                      {isSelectMode && (
                        <td style={{ textAlign: "center" }}>
                          <input
                            type="checkbox"
                            className="pp-checkbox"
                            checked={selectedIds.has(p.id)}
                            onChange={() => toggleSelectOne(p.id)}
                            aria-label={`Select ${p.productName || p.designNumber}`}
                          />
                        </td>
                      )}
                      <td>{p.productName || "-"}</td>
                      <td>{p.designNumber || "-"}</td>
                      <td>{formatDate(p.date)}</td>
                      <td>
                        <span
                          className={`pp-ratePill ${
                            priceMode === "W" ? "pp-ratePillW" : "pp-ratePillX"
                          }`}
                        >
                          ₹{getSelectedRate(p).toLocaleString("en-IN")}
                        </span>
                      </td>
                      <td>
                        <div className="pp-actionBtns">
                          <button
                            className="pp-editBtn"
                            onClick={() => handleEdit(p)}
                            type="button"
                            disabled={isCurrentlyOffline}
                            title={
                              isCurrentlyOffline
                                ? "Offline mode mein edit disabled hai"
                                : "Edit product"
                            }
                          >
                            <FaEdit size={12} />
                            Edit
                          </button>
                          <button
                            className="pp-deleteBtn"
                            onClick={() => handleDelete(p)}
                            type="button"
                            disabled={isCurrentlyOffline}
                            title={
                              isCurrentlyOffline
                                ? "Offline mode mein delete disabled hai"
                                : "Delete product"
                            }
                          >
                            <FaTrash size={11} />
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <div className="pp-empty">
            <FaBox className="pp-emptyIcon" />
            <p className="pp-emptyText">
              No products found. Try adjusting filters or search.
            </p>
          </div>
        )}

        {/* Pagination */}
        {filtered.length > 0 && (
          <div className="pp-pagination">
            <div className="pp-pageInfoWrap">
              <p className="pp-metaText" style={{ margin: 0 }}>
                {isAllSelected ? (
                  <span>Showing all <b>{filtered.length.toLocaleString()}</b> records</span>
                ) : (
                  <span>
                    Page <b>{currentPage}</b> of <b>{totalPages.toLocaleString()}</b> ({filtered.length.toLocaleString()} records)
                  </span>
                )}
              </p>
              <div className="pp-pageSizeSelectWrap">
                <span>Per page:</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="pp-pageSizeSelect"
                >
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={200}>200</option>
                  <option value={-1}>All ({filtered.length.toLocaleString()})</option>
                </select>
              </div>
            </div>

            {totalPages > 1 && !isAllSelected && (
              <div className="pp-pageBtns">
                <button
                  className="pp-pageBtn"
                  disabled={currentPage === 1}
                  onClick={() => {
                    setCurrentPage((p) => Math.max(1, p - 1));
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                  type="button"
                >
                  <FaChevronLeft size={11} /> Prev
                </button>

                <span className="pp-pagePill">
                  {currentPage} / {totalPages.toLocaleString()}
                </span>

                <button
                  className="pp-pageBtn"
                  disabled={currentPage === totalPages}
                  onClick={() => {
                    setCurrentPage((p) => Math.min(totalPages, p + 1));
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                  type="button"
                >
                  Next <FaChevronRight size={11} />
                </button>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Modals */}
      <ProductModal
        open={showModal}
        onClose={() => setShowModal(false)}
        onSave={handleSave}
        isSaving={isSaving}
        editId={editId}
        form={form}
        setForm={setForm}
        productNameList={productNameList}
      />

      <ProvisionalInvoiceModal
        open={showInvoice}
        onClose={() => setShowInvoice(false)}
        products={data}
        user={user}
      />

      <Footer />
    </div>
  );
}

export default ProductPage;
